import { Inject, Injectable } from '@angular/core';
import { CatalogFilters } from '../shared/models';

export interface CatalogState {
  currentPage: number;
  filters: CatalogFilters;
}

/**
 * Holds the Course Catalog pagination + filter/sort state in memory so it can
 * be restored when the user returns to the catalog from a course (UT-3134).
 *
 * The state is intentionally kept in memory only: reloading, closing the tab,
 * or logging out naturally destroys it. It is also cleared explicitly whenever
 * the user navigates out of the Academy menu, via a UI-Router transition hook
 * (mirrors the `state.includes.academy` matching used in routes/courses.ts).
 */
@Injectable({ providedIn: 'root' })
export class AcademyCatalogStateService {
  private state: CatalogState | null = null;

  constructor(@Inject('$transitions') private $transitions: any) {
    // `state` here is the UI-Router StateObject, whose `.includes` map is true
    // for any academy descendant (course/quiz/tracks states are all parented to
    // 'academy'). Mirrors the matcher in routes/courses.ts — keep them in sync.
    // Root singleton: the hook intentionally lives for the app's lifetime, so we
    // do not deregister it.
    this.$transitions.onSuccess(
      { to: (state: any) => !state.includes?.academy },
      () => this.clear(),
    );
  }

  get(): CatalogState | null {
    return this.state;
  }

  save(currentPage: number, filters: CatalogFilters): void {
    this.state = { currentPage, filters: { ...filters } };
  }

  clear(): void {
    this.state = null;
  }
}
